
import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { uploadsService } from "@/services/uploadsService";

type ChatProfilePicDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentAvatarUrl: string;
  onUpdateAvatar: (url: string) => void;
};

export default function ChatProfilePicDialog({
  isOpen,
  onOpenChange,
  currentAvatarUrl,
  onUpdateAvatar
}: ChatProfilePicDialogProps) {
  const [newAvatarUrl, setNewAvatarUrl] = useState("");
  const { toast } = useToast();
  
  const handleProfileUpdate = () => {
    if (newAvatarUrl) {
      onUpdateAvatar(newAvatarUrl);
      onOpenChange(false);
      toast({
        title: "Profile picture updated",
        description: "Profile picture has been changed successfully",
      });
    } else {
      toast({
        title: "Error",
        description: "Please enter a valid URL for the profile picture",
        variant: "destructive"
      });
    }
  };
  
  // Generate random avatar via backend
  const generateRandomAvatar = async () => {
    try {
      // uploadsService returns response.data.data directly (already unwrapped by apiService)
      const response = await uploadsService.generateAvatar('svg');
      const randomAvatarUrl = response?.avatar_url || response?.avatarUrl;
      
      if (randomAvatarUrl) {
        setNewAvatarUrl(randomAvatarUrl);
      }
    } catch (error) {
      console.error('Error generating avatar:', error);
      toast({
        title: "Error",
        description: "Failed to generate avatar. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Profile Picture</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col gap-4 py-4">
          <div className="flex justify-center">
            <Avatar className="w-24 h-24">
              <AvatarImage src={newAvatarUrl || currentAvatarUrl} />
              <AvatarFallback><User className="h-12 w-12" /></AvatarFallback>
            </Avatar>
          </div>
          
          <div className="flex flex-col gap-2">
            <Input
              placeholder="Enter avatar URL"
              value={newAvatarUrl}
              onChange={(e) => setNewAvatarUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Enter a URL for your profile picture or generate a random one
            </p>
          </div>
          
          <div className="flex justify-between">
            <Button variant="outline" onClick={generateRandomAvatar}>
              Generate Random
            </Button>
            <Button onClick={handleProfileUpdate} disabled={!newAvatarUrl}>
              Update Profile
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
